/**
 * Hats, drawn in the blob's own 100-unit space so they squash, hop and flip
 * with it. Origin is the top centre of the head; one unit is ~1/32 of the
 * body's half-width. Flat fills, a darker band and a soft highlight, the same
 * vocabulary as the blobs: no outlines.
 */
import type { HatId } from '../../../shared/cosmetics.ts'
import type { BlobArt } from './blob.ts'
import { shade } from './color.ts'

type Ctx = CanvasRenderingContext2D

function ell(ctx: Ctx, x: number, y: number, rx: number, ry: number, fill: string, rot = 0): void {
  ctx.beginPath(); ctx.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2); ctx.fillStyle = fill; ctx.fill()
}
function circ(ctx: Ctx, x: number, y: number, r: number, fill: string): void { ell(ctx, x, y, r, r, fill) }
function shape(ctx: Ctx, pts: number[], fill: string): void {
  ctx.beginPath(); ctx.moveTo(pts[0]!, pts[1]!)
  for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i]!, pts[i + 1]!)
  ctx.closePath(); ctx.fillStyle = fill; ctx.fill()
}
function rrect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number, fill: string): void {
  ctx.beginPath(); ctx.roundRect(x, y, w, h, r); ctx.fillStyle = fill; ctx.fill()
}
/** Upper half-dome sitting on y = base. */
function dome(ctx: Ctx, base: number, rx: number, ry: number, fill: string): void {
  ctx.beginPath(); ctx.ellipse(0, base, rx, ry, 0, Math.PI, 0); ctx.closePath(); ctx.fillStyle = fill; ctx.fill()
}
const shine = (ctx: Ctx, x: number, y: number, rx: number, ry: number, rot = -0.5) => ell(ctx, x, y, rx, ry, 'rgba(255,255,255,0.35)', rot)

const HATS: Record<Exclude<HatId, 'none'>, (ctx: Ctx, t: number, body: string) => void> = {
  party(ctx) {
    ctx.save(); ctx.rotate(0.18)
    shape(ctx, [-13, 6, 13, 6, 1, -36], '#ff6f9c')
    ctx.save(); ctx.beginPath(); ctx.moveTo(-13, 6); ctx.lineTo(13, 6); ctx.lineTo(1, -36); ctx.closePath(); ctx.clip()
    for (const y of [-2, -14, -26]) shape(ctx, [-20, y, 20, y - 6, 20, y - 1, -20, y + 5], '#ffd166')
    ctx.restore()
    circ(ctx, 1, -37, 5.5, '#7ee0c3')
    shine(ctx, -3, -10, 2.5, 7, 0.2)
    ctx.restore()
  },
  tophat(ctx) {
    ell(ctx, 0, 3, 27, 6.5, '#2d2a3e')
    rrect(ctx, -16, -30, 32, 34, 4, '#3a3552')
    rrect(ctx, -16, -6, 32, 7, 1, '#e0566f')
    ell(ctx, 0, -30, 16, 4, '#4a4466')
    shine(ctx, -9, -16, 2.5, 9, 0)
  },
  beanie(ctx) {
    dome(ctx, 8, 31, 28, '#4fb3c9')
    ctx.strokeStyle = 'rgba(0,0,0,0.12)'; ctx.lineWidth = 2
    for (const x of [-18, -9, 0, 9, 18]) { ctx.beginPath(); ctx.moveTo(x, 4); ctx.quadraticCurveTo(x * 0.8, -10, x * 0.5, -16); ctx.stroke() }
    rrect(ctx, -32, 0, 64, 11, 5.5, '#3a95aa')
    circ(ctx, 0, -22, 7, '#ffd166')
    shine(ctx, -14, -8, 4, 7)
  },
  crown(ctx) {
    shape(ctx, [-20, 5, 20, 5, 22, -22, 11, -9, 0, -27, -11, -9, -22, -22], '#ffc93c')
    rrect(ctx, -20, -2, 40, 8, 2, '#f5a623')
    circ(ctx, 0, 2, 3.2, '#e0566f'); circ(ctx, -12, 2, 2.6, '#4fc3f7'); circ(ctx, 12, 2, 2.6, '#7cf29c')
    for (const [x, y] of [[-22, -22], [0, -27], [22, -22]] as const) circ(ctx, x, y, 3, '#fff1a8')
    shine(ctx, -12, -8, 2, 6, 0.3)
  },
  cowboy(ctx) {
    ctx.beginPath(); ctx.moveTo(-42, -2); ctx.quadraticCurveTo(-40, 10, 0, 9); ctx.quadraticCurveTo(40, 10, 42, -2)
    ctx.quadraticCurveTo(30, 4, 0, 3); ctx.quadraticCurveTo(-30, 4, -42, -2); ctx.fillStyle = '#a8673d'; ctx.fill()
    ctx.beginPath(); ctx.moveTo(-18, 4); ctx.quadraticCurveTo(-20, -24, -8, -22); ctx.quadraticCurveTo(0, -16, 8, -22); ctx.quadraticCurveTo(20, -24, 18, 4); ctx.closePath()
    ctx.fillStyle = '#bf7a4a'; ctx.fill()
    rrect(ctx, -18, -3, 36, 6, 2, '#6b3f22')
    shine(ctx, -10, -12, 2.5, 7, 0.1)
  },
  chef(ctx) {
    rrect(ctx, -17, -10, 34, 15, 3, '#f4f1ec')
    for (const [x, y, r] of [[-13, -18, 11], [0, -24, 13], [13, -18, 11]] as const) circ(ctx, x, y, r, '#ffffff')
    ctx.strokeStyle = 'rgba(0,0,0,0.08)'; ctx.lineWidth = 1.5
    for (const x of [-8, 0, 8]) { ctx.beginPath(); ctx.moveTo(x, -8); ctx.lineTo(x, 4); ctx.stroke() }
  },
  flower(ctx, t) {
    ctx.save(); ctx.translate(15, 0); ctx.rotate(Math.sin(t * 1.6) * 0.08)
    ell(ctx, -7, 4, 8, 4, '#5cc98a', 0.5)
    for (let i = 0; i < 5; i++) { const a = (i / 5) * Math.PI * 2; circ(ctx, Math.cos(a) * 7, -4 + Math.sin(a) * 7, 6, '#ff8fab') }
    circ(ctx, 0, -4, 4.5, '#ffd166')
    ctx.restore()
  },
  propeller(ctx, t) {
    ctx.save(); ctx.beginPath(); ctx.ellipse(0, 7, 28, 24, 0, Math.PI, 0); ctx.closePath(); ctx.clip()
    const cols = ['#ff6f6f', '#ffd166', '#4fc3f7', '#7cf29c']
    for (let i = 0; i < 4; i++) shape(ctx, [0, 7, -40 + i * 20, -30, -20 + i * 20, -30], cols[i]!)
    ctx.restore()
    rrect(ctx, -1.5, -27, 3, 10, 1, '#3a3552')
    const k = Math.cos(t * 18)
    ell(ctx, 0, -27, 22 * Math.abs(k) + 2, 3, '#e0566f')
    circ(ctx, 0, -27, 3, '#3a3552')
    rrect(ctx, 6, 2, 24, 5, 2.5, '#ff6f6f')
  },
  wizard(ctx, t) {
    ell(ctx, 0, 4, 31, 7, '#5a4bb5')
    ctx.beginPath(); ctx.moveTo(-19, 4); ctx.quadraticCurveTo(-6, -24, 4, -46); ctx.quadraticCurveTo(14, -50, 22, -44)
    ctx.quadraticCurveTo(10, -40, 8, -30); ctx.quadraticCurveTo(14, -10, 19, 4); ctx.closePath(); ctx.fillStyle = '#6c5ce7'; ctx.fill()
    const tw = 0.6 + 0.4 * Math.sin(t * 3)
    ctx.globalAlpha *= tw
    for (const [x, y, r] of [[-4, -10, 3.4], [6, -24, 2.6], [-8, 0, 2]] as const) {
      ctx.save(); ctx.translate(x, y); ctx.rotate(0.3)
      shape(ctx, [0, -r * 1.6, r * 0.45, -r * 0.45, r * 1.6, 0, r * 0.45, r * 0.45, 0, r * 1.6, -r * 0.45, r * 0.45, -r * 1.6, 0, -r * 0.45, -r * 0.45], '#ffe680')
      ctx.restore()
    }
    ctx.globalAlpha /= tw
  },
  bunny(ctx, t) {
    const w = Math.sin(t * 2.2) * 0.06
    for (const s of [-1, 1]) {
      ctx.save(); ctx.translate(s * 11, 4); ctx.rotate(s * 0.22 + w * s)
      ell(ctx, 0, -20, 8, 22, '#f7f2ee')
      ell(ctx, 0, -18, 4, 15, '#ffb3c7')
      ctx.restore()
    }
  },
  cat(ctx, _t, body) {
    for (const s of [-1, 1]) {
      shape(ctx, [s * 7, 6, s * 26, 4, s * 21, -18], body)
      shape(ctx, [s * 11, 4, s * 22, 3, s * 19.5, -10], '#ffb3c7')
    }
  },
  halo(ctx, t) {
    const y = -14 + Math.sin(t * 2) * 2
    ctx.save()
    ctx.shadowColor = 'rgba(255,220,120,0.9)'; ctx.shadowBlur = 8
    ctx.beginPath(); ctx.ellipse(0, y, 20, 6, 0, 0, Math.PI * 2); ctx.lineWidth = 4.5; ctx.strokeStyle = '#ffd95c'; ctx.stroke()
    ctx.restore()
  },
  frog(ctx) {
    dome(ctx, 9, 31, 22, '#62c370')
    rrect(ctx, -31, 3, 62, 8, 4, '#4ea85c')
    for (const s of [-1, 1]) { circ(ctx, s * 13, -14, 9, '#62c370'); circ(ctx, s * 13, -15, 6.5, '#ffffff'); circ(ctx, s * 13 + 1, -14, 3.2, '#2d2a3e') }
    ell(ctx, 0, -2, 7, 2, '#e0566f')
    shine(ctx, -16, -2, 3, 5)
  },
  sprout(ctx, t) {
    ctx.save(); ctx.rotate(Math.sin(t * 1.8) * 0.12)
    ctx.strokeStyle = '#4fa765'; ctx.lineWidth = 3; ctx.lineCap = 'round'
    ctx.beginPath(); ctx.moveTo(0, 4); ctx.quadraticCurveTo(-2, -8, 0, -16); ctx.stroke()
    ell(ctx, -8, -18, 9, 5, '#6ad89a', 0.5)
    ell(ctx, 8, -20, 9, 5, '#5cc98a', -0.5)
    ctx.restore()
  },
  bow(ctx) {
    ctx.save(); ctx.translate(14, 0); ctx.rotate(-0.25)
    shape(ctx, [0, 0, -15, -10, -14, 9], '#ff5d8f')
    shape(ctx, [0, 0, 15, -10, 14, 9], '#ff5d8f')
    shape(ctx, [0, 0, -9, -5, -8, 4], '#e0436f')
    shape(ctx, [0, 0, 9, -5, 8, 4], '#e0436f')
    circ(ctx, 0, 0, 4.5, '#ff8fab')
    ctx.restore()
  },
  viking(ctx) {
    for (const s of [-1, 1]) {
      ctx.beginPath(); ctx.moveTo(s * 24, 0); ctx.quadraticCurveTo(s * 40, -6, s * 38, -30); ctx.quadraticCurveTo(s * 32, -14, s * 20, -10); ctx.closePath()
      ctx.fillStyle = '#f3ead6'; ctx.fill()
    }
    dome(ctx, 7, 29, 24, '#9aa3b5')
    rrect(ctx, -30, 1, 60, 8, 3, '#c9a24a')
    rrect(ctx, -3, -17, 6, 20, 2, '#c9a24a')
    shine(ctx, -13, -7, 3.5, 6)
  },
  headphones(ctx) {
    ctx.strokeStyle = '#3a3552'; ctx.lineWidth = 5; ctx.lineCap = 'round'
    ctx.beginPath(); ctx.ellipse(0, 18, 33, 30, 0, Math.PI * 1.08, Math.PI * 1.92); ctx.stroke()
    for (const s of [-1, 1]) { rrect(ctx, s * 33 - 7, 16, 14, 20, 6, '#ff6f9c'); rrect(ctx, s * 33 - 4, 19, 8, 14, 4, '#3a3552') }
  },
  beret(ctx) {
    ctx.save(); ctx.rotate(-0.18)
    ell(ctx, 2, -1, 29, 10, '#d94a5a')
    ell(ctx, 2, 2, 25, 6, '#b83a4a')
    rrect(ctx, 0, -14, 3, 6, 1.5, '#b83a4a')
    shine(ctx, -10, -5, 6, 2.2, -0.1)
    ctx.restore()
  },
  cap(ctx) {
    ell(ctx, 22, 5, 20, 5, '#2f6fd6', -0.08)
    dome(ctx, 7, 29, 24, '#3d85ff')
    ctx.strokeStyle = 'rgba(0,0,0,0.12)'; ctx.lineWidth = 1.5
    ctx.beginPath(); ctx.moveTo(0, -17); ctx.lineTo(0, 6); ctx.stroke()
    circ(ctx, 0, -17, 2.6, '#2f6fd6')
    shine(ctx, -12, -6, 4, 6)
  },
  mushroom(ctx) {
    ctx.beginPath(); ctx.moveTo(-34, 6); ctx.quadraticCurveTo(-32, -28, 0, -28); ctx.quadraticCurveTo(32, -28, 34, 6); ctx.quadraticCurveTo(0, 0, -34, 6)
    ctx.fillStyle = '#e8484f'; ctx.fill()
    for (const [x, y, r] of [[-16, -12, 5], [4, -20, 4.5], [18, -6, 4], [-2, -6, 3.2]] as const) circ(ctx, x, y, r, '#fff6ec')
  },
  snorkel(ctx) {
    rrect(ctx, -32, 2, 64, 6, 3, '#3a3552')
    for (const s of [-1, 1]) { circ(ctx, s * 11, 0, 9.5, '#7f8aa3'); circ(ctx, s * 11, 0, 7, shade('#9fe3ff', 0.1)); shine(ctx, s * 11 - 3, -3, 2, 3.5) }
    rrect(ctx, -3, -1, 6, 3, 1.5, '#7f8aa3')
  },
}

/** A hat on its own (hat stands), centred at (x, y) = the top of whatever wears it. */
export function drawHatAt(ctx: Ctx, hat: HatId, x: number, y: number, scale: number, t: number, body: string): void {
  if (hat === 'none') return
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale, scale)
  HATS[hat](ctx, t, body)
  ctx.restore()
}

export function drawHat(ctx: Ctx, hat: HatId, art: BlobArt, t: number): void {
  if (hat === 'none') return
  const f = art.frame
  ctx.save()
  ctx.translate(f.cx, f.cy - f.ry * 0.86)
  const s = Math.max(0.6, Math.min(1.25, f.rx / 32))
  ctx.scale(s, s)
  HATS[hat](ctx, t, art.color)
  ctx.restore()
}
